/* eslint-disable react-refresh/only-export-components */
import { Input as BaseInput } from '@base-ui/react/input'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@eigtech/ui/lib/utils'

// Define input size variants
const inputVariants = cva(
  `border-input text-foreground placeholder:text-muted-foreground/80 focus-visible:ring-ring/30 focus-visible:border-ring [[readonly]]:bg-muted/80 file:border-input file:text-foreground aria-invalid:border-destructive/60 aria-invalid:ring-destructive/10 dark:aria-invalid:border-destructive dark:aria-invalid:ring-destructive/20 shadow-xs [[readonly]]:cursor-not-allowed [[type=file]]:py-0 flex w-full border bg-transparent shadow-black/5 transition-[color,box-shadow] file:h-full file:border-0 file:border-e file:border-solid file:bg-transparent file:p-0 file:font-medium file:not-italic focus-visible:outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-60`,
  {
    variants: {
      variant: {
        lg: 'h-10 rounded-md px-4 text-sm file:me-4 file:pe-4',
        md: 'h-9 rounded-md px-3 text-sm file:me-3 file:pe-3',
        sm: 'h-8 rounded-md px-2.5 text-xs file:me-2.5 file:pe-2.5',
      },
    },
    defaultVariants: {
      variant: 'md',
    },
  }
)

const inputAddonVariants = cva(
  'bg-muted border-input text-secondary-foreground [&_svg]:text-secondary-foreground/60 shadow-xs flex shrink-0 items-center justify-center border shadow-[rgba(0,0,0,0.05)]',
  {
    variants: {
      variant: {
        lg: '[&_svg:not([class*=size-])]:size-4.5 h-10 min-w-10 rounded-md px-4 text-sm',
        md: '[&_svg:not([class*=size-])]:size-4.5 h-9 min-w-9 rounded-md px-3 text-sm',
        sm: 'h-8 min-w-7 rounded-md px-2.5 text-xs [&_svg:not([class*=size-])]:size-3.5',
      },
      mode: {
        default: '',
        icon: 'justify-center px-0',
      },
    },
    defaultVariants: {
      variant: 'md',
      mode: 'default',
    },
  }
)

const inputGroupVariants = cva(
  cn(
    'flex items-stretch',
    '**:data-[slot=input-wrapper]:grow [&_[data-slot=button]+[data-slot=input-wrapper]]:rounded-s-none [&_[data-slot=input-addon]+[data-slot=input-wrapper]]:rounded-s-none [&_[data-slot=input-addon]:has(+[data-slot=input-wrapper])]:rounded-e-none [&_[data-slot=input-addon]:has(+[data-slot=input-wrapper])]:border-e-0 [&_[data-slot=input-wrapper]+[data-slot=button]]:rounded-s-none [&_[data-slot=input-wrapper]+[data-slot=input-addon]]:rounded-s-none [&_[data-slot=input-wrapper]+[data-slot=input-addon]]:border-s-0 [&_[data-slot=input-wrapper]:has(+[data-slot=button])]:rounded-e-none [&_[data-slot=input-wrapper]:has(+[data-slot=input-addon])]:rounded-e-none',
    '**:data-[slot=input]:grow [&_[data-slot=button]+[data-slot=input]]:rounded-s-none [&_[data-slot=input-addon]+[data-slot=input]]:rounded-s-none [&_[data-slot=input-addon]:has(+[data-slot=input])]:rounded-e-none [&_[data-slot=input-addon]:has(+[data-slot=input])]:border-e-0 [&_[data-slot=input]+[data-slot=button]]:rounded-s-none [&_[data-slot=input]+[data-slot=input-addon]]:rounded-s-none [&_[data-slot=input]+[data-slot=input-addon]]:border-s-0 [&_[data-slot=input]:has(+[data-slot=button])]:rounded-e-none [&_[data-slot=input]:has(+[data-slot=input-addon])]:rounded-e-none',
    '**:data-[slot=datefield]:grow [&_[data-slot=datefield]:has(~[data-slot=input-addon])]:**:data-[slot=input]:rounded-e-none [&_[data-slot=input-addon]+[data-slot=datefield]]:**:data-[slot=input]:rounded-s-none [&_[data-slot=datefield]+[data-slot=input-addon]]:rounded-s-none [&_[data-slot=datefield]+[data-slot=input-addon]]:border-s-0 [&_[data-slot=datefield]~[data-slot=input-addon]]:rounded-s-none [&_[data-slot=input-addon]:has(+[data-slot=datefield])]:rounded-e-none [&_[data-slot=input-addon]:has(+[data-slot=datefield])]:border-e-0',
    '[&_[data-slot=input-addon]:has(+[data-slot=button])]:rounded-e-none'
  ),
  {
    variants: {},
    defaultVariants: {},
  }
)

const inputWrapperVariants = cva(
  `has-focus-visible:ring-ring/30 has-focus-visible:border-ring **:data-[slot=input]:text-foreground **:data-[slot=input]:placeholder:text-muted-foreground [&_svg]:text-muted-foreground has-aria-invalid:border-destructive/60 has-aria-invalid:ring-destructive/10 dark:has-aria-invalid:border-destructive dark:has-aria-invalid:ring-destructive/20 has-focus-visible:ring-[3px] has-focus-visible:outline-none **:data-[slot=datefield]:grow **:data-[slot=input]:flex **:data-[slot=input]:h-auto **:data-[slot=input]:w-full **:data-[slot=input]:border-0 **:data-[slot=input]:bg-transparent **:data-[slot=input]:p-0 **:data-[slot=input]:shadow-none **:data-[slot=input]:transition-colors **:data-[slot=input]:outline-none **:data-[slot=input]:focus-visible:ring-0 **:data-[slot=input]:disabled:cursor-not-allowed **:data-[slot=input]:disabled:opacity-50 **:data-[slot=input]:data-focus-within:border-0 **:data-[slot=input]:data-focus-within:ring-0 **:data-[slot=input]:data-focus-within:ring-transparent flex items-center gap-1.5`,
  {
    variants: {
      variant: {
        sm: 'gap-1.25 [&_svg:not([class*=size-])]:size-3.5',
        md: 'gap-1.5 [&_svg:not([class*=size-])]:size-4',
        lg: 'gap-1.5 [&_svg:not([class*=size-])]:size-4',
      },
    },
    defaultVariants: {
      variant: 'md',
    },
  }
)

function Input({
  className,
  type,
  variant,
  ...props
}: React.ComponentProps<typeof BaseInput> & VariantProps<typeof inputVariants>) {
  return (
    <BaseInput
      className={cn(inputVariants({ variant }), className)}
      data-slot="input"
      type={type}
      {...props}
    />
  )
}

function InputAddon({
  className,
  variant,
  mode,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputAddonVariants>) {
  return (
    <div
      className={cn(inputAddonVariants({ variant, mode }), className)}
      data-slot="input-addon"
      {...props}
    />
  )
}

function InputGroup({
  className,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupVariants>) {
  return <div className={cn(inputGroupVariants(), className)} data-slot="input-group" {...props} />
}

function InputWrapper({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputWrapperVariants>) {
  return (
    <div
      className={cn(inputVariants({ variant }), inputWrapperVariants({ variant }), className)}
      data-slot="input-wrapper"
      {...props}
    />
  )
}

export { Input, InputAddon, inputAddonVariants, InputGroup, inputVariants, InputWrapper }
